import { purgeStoredState } from 'redux-persist';
import get from 'lodash/get';
import set from 'lodash/set';
import { AuthenticationDetails } from 'amazon-cognito-identity-js';

import persistConfig from '../../persistConfig';
import history from '../../history';
import cognitoService from '../../cognito/cognito-service';
import communityLogout from '../Community/logout';
import loginMutation from './loginMutation';
import * as constants from './constants';
import { reload, isDeepLink, _deserialize, getUri, isMockingNavianceLogin, getLocationSearch } from '../../utils';
import idpService from '../../idp';

const jwtDecode = require('jwt-decode');

let refreshTimeout;

/**
 *
 * @param {*} viewer
 * Returns decoded token
 */
const getIdToken = (viewer) => (
  viewer ? viewer.idToken : null
);

/**
 *
 * @param {*} viewer
 * Returns original JWT token
 */
const getJwtToken = (viewer) => (
  viewer ? viewer.jwtToken : null
);

const logoutSuccess = () => (
  {
    type: constants.LOGOUT_USER_SUCCESS,
  }
);

const logoutNonNaviance = (viewer) => (
  async (dispatch) => {
    cognitoService.logout();
    if (get(getIdToken(viewer), 'sub')) {
      await communityLogout(get(getIdToken(viewer), 'sub'));
    }
    clearTimeout(refreshTimeout);
    dispatch(logoutSuccess());
    return purgeStoredState(persistConfig).then(() => {
      reload();
    });
  }
);

const loginUserSuccess = (payload) => (
  {
    type: constants.LOGIN_USER_SUCCESS,
    payload,
  }
);

const loginUserFailure = () => (
  {
    type: constants.LOGIN_USER_FAILURE,
  }
);

const newPasswordRequired = (payload) => (
  {
    type: constants.LOGIN_USER_NEW_PASSWORD_REQUIRED,
    payload,
  }
);

const logoutNaviance = (viewer) => (
  async (dispatch) => {
    if (get(getIdToken(viewer), 'sub')) {
      await communityLogout(get(getIdToken(viewer), 'sub'));
    }
    idpService.logout();
    dispatch(logoutSuccess());
    return purgeStoredState(persistConfig).then(() => {
      reload();
    });
  }
);

const signOut = (viewer) => (viewer.isNaviance ? logoutNaviance(viewer) : logoutNonNaviance(viewer));

const navianceSignInFailure = () => (
  async (dispatch) => {
    dispatch(loginUserFailure());
    idpService.clear();
    await purgeStoredState(persistConfig);
    history.push('/error');
  }
);

function setupSentryUserContext(viewer) {
  if (window.Sentry) {
    const idToken = get(viewer, 'idToken', {});
    window.Sentry.setUser({
      id: idToken.sub,
      email: idToken.email,
      username: viewer.userName,
    });
    window.Sentry.setExtra('isNaviance', viewer.isNaviance);
    window.Sentry.setExtra('schoolId', idToken[constants.SCHOOL_ID] || idToken['custom:institution']);
  }
}

const navianceTokenInSuccess = (userData) => (
  (dispatch) => {
    userData.loginTime = Date.now();
    userData.isNaviance = true;
    userData.userName = userData.idToken.sub;
    userData.isAuthenticated = true;
    setupSentryUserContext(userData);
    dispatch(loginUserSuccess(userData));
    if (!isDeepLink()) {
      history.push('/');
    }
  }
);

const navianceCodeInSuccess = () => (
  async (dispatch) => {
    try {
      const data = await idpService.acquireToken();
      const idToken = get(data, 'idToken', null);
      if (!idToken || !idToken[constants.SCHOOL_ID]) {
        dispatch(navianceSignInFailure());
      } else {
        dispatch(navianceTokenInSuccess(data));
      }
    } catch (err) {
      dispatch(navianceSignInFailure());
    }
  }
);

const loginSuccess = (session, loginTime) => {
  const jwtToken = session.getIdToken().getJwtToken();
  const idTokenDecoded = jwtDecode(jwtToken);
  const payload = {
    jwtToken,
  };
  payload.idToken = idTokenDecoded;
  payload.isAuthenticated = true;
  payload.userName = idTokenDecoded['cognito:username'];
  payload.loginTime = loginTime;
  payload.errorCode = '';
  payload.isNaviance = false;
  return loginUserSuccess(payload);
};

const loginFailure = (err) => {
  let errorCode = err.code;
  if (errorCode === 'UserLambdaValidationException') {
    errorCode = err.message.includes('locked') ? 'UserIsLocked' : 'InvalidPasswordException';
  }
  if (errorCode === 'NotAuthorizedException') {
    errorCode = 'LoginFailedNotAuthorizedException';
  }
  const payload = {
    errorCode,
  };
  return {
    type: constants.LOGIN_USER_FAILURE,
    payload,
  };
};

const completeNewPassword = (user) => (
  (dispatch) => {
    const cognitoUser = cognitoService.getCognitoUser(user.username);
    cognitoUser.Session = user.Session;
    cognitoUser.completeNewPasswordChallenge(user.password, {}, {
      onSuccess: (result) => handleNewLoginSuccess(dispatch, result, Date.now()), // eslint-disable-line no-use-before-define
      onFailure: (err) => dispatch(loginFailure(err)),
    });
  }
);

const refreshSession = (viewer) => (dispatch) => {
  // can't refresh impersonator token or naviance user, log the user out
  const impersonator = get(viewer, 'idToken.impersonator');
  if (impersonator || viewer.isNaviance) {
    return dispatch(logoutNonNaviance(viewer)).then(() => Promise.reject(new Error('Not Supported')));
  }
  return cognitoService.getCurrentUserWithSession()
    .then((currentUser) => {
      const session = currentUser.getSignInUserSession();
      const { loginTime } = viewer; // maintain original login time
      if (viewer.jwtToken !== session.getIdToken().getJwtToken()) {
        return Promise.resolve(handleLoginSuccess(dispatch, session, loginTime, { viewer })); // eslint-disable-line no-use-before-define
      } else {
        return new Promise((resolve, reject) => {
          currentUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
            if (err) {
              reject(new Error('Could not create user session'));
            } else {
              resolve(handleLoginSuccess(dispatch, newSession, loginTime, { viewer })); // eslint-disable-line no-use-before-define
            }
          });
        });
      }
    })
    .catch((err) => {
      console.log(err); // eslint-disable-line no-console
      return dispatch(logoutNonNaviance(viewer));
    });
};

function setupTokenRefreshTimer(dispatch, viewer) {
  const expirationMs = get(getIdToken(viewer), 'exp', 0) * 1000;
  const timeout = expirationMs - Date.now() - (2000 * 60); // refresh 2 minutes before expiration
  // const timeout = 2000; // refresh 2 minutes before expiration
  if (timeout < 0) {
    return dispatch(refreshSession(viewer));
  } else {
    refreshTimeout = setTimeout(() => {
      dispatch(refreshSession(viewer));
    }, Math.min(timeout, 0x7FFFFFFF));
    return Promise.resolve();
  }
}

// starts naviance oauth2 flow.
const loginNaviance = () =>
  (dispatch) => {
    idpService.login();
  };

const appRefreshed = (viewer) => async (dispatch) => {
  const query = getLocationSearch();
  const parameters = _deserialize(query);
  const isNavianceRedirect = Object.prototype.hasOwnProperty.call(parameters, 'naviance');
  const uri = getUri();
  const hashIndex = uri.indexOf('#');
  const angularIndex = uri.indexOf('#!');
  if (hashIndex !== -1 && angularIndex === -1) {
    const jwtToken = uri.substring(hashIndex + 1);
    const idToken = jwtDecode(jwtToken);

    // naviance mock login
    if (isMockingNavianceLogin() && idToken.mock_naviance_token) {
      return dispatch(navianceTokenInSuccess({ idToken, jwtToken }));
    }

    // impersonation
    /* istanbul ignore else */
    if (idToken.impersonator) {
      // Is Naviance
      if (Object.keys(idToken).includes(constants.SCHOOL_ID)) {
        return dispatch(navianceTokenInSuccess({ idToken, jwtToken }));
      }

      const dummySession = {
        getIdToken: () => ({
          getJwtToken: () => jwtToken,
        }),
      };

      history.push('/');
      return dispatch(loginSuccess(dummySession, Date.now(), { viewer }));
    }
  }
  if (isNavianceRedirect) {
    dispatch(loginNaviance());
  } else if (viewer.isAuthenticated && !viewer.isNaviance) {
    if (!isDeepLink()) {
      history.push('/');
    }
    setupSentryUserContext(viewer);
    return setupTokenRefreshTimer(dispatch, viewer);
  } else if (viewer.isAuthenticated && viewer.isNaviance) {
    if (!isDeepLink()) {
      history.push('/');
    }
    setupSentryUserContext(viewer);
  } else if (!isDeepLink()) {
    history.push('/login'); // takes care of root url
  }
  return null;
};

function handleLoginSuccess(dispatch, session, loginTime) {
  const loginSuccessRedux = dispatch(loginSuccess(session, loginTime));
  setupSentryUserContext(loginSuccessRedux.payload);
  setupTokenRefreshTimer(dispatch, loginSuccessRedux.payload);
  if (!isDeepLink()) {
    history.push('/');
  }
  return loginSuccessRedux;
}

function recordLogin(token) {
  // breaks circular dependency
  // eslint-disable-next-line global-require
  const getApolloClient = require('../../apollo-client').default;
  const client = getApolloClient({
    getState: () => set({}, 'current.viewer.jwtToken', token),
  });
  return client.mutate({
    mutation: loginMutation,
    variables: {
      input: {},
    },
  }).catch((e) => console.error(e)); // eslint-disable-line no-console
}

function handleNewLoginSuccess(dispatch, session, loginTime) {
  return recordLogin(session.getIdToken().getJwtToken())
    .then(() => handleLoginSuccess(dispatch, session, loginTime));
}

const login = (user) => (
  (dispatch) => {
    const cognitoUser = cognitoService.getCognitoUser(user.username);
    const authenticationDetails = new AuthenticationDetails({
      Username: user.username,
      Password: user.password,
    });
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (payload) => handleNewLoginSuccess(dispatch, payload, Date.now()),
      onFailure: (err) => dispatch(loginFailure(err)),
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        dispatch(newPasswordRequired({
          cognitoUser: {
            username: cognitoUser.username,
            Session: cognitoUser.Session,
            email: user.username,
          },
        }));
        history.push('/new-password');
      },
    });
  }
);
export { navianceTokenInSuccess, loginUserFailure, loginUserSuccess, logoutSuccess, loginFailure };
export { loginNaviance, signOut, logoutNonNaviance, navianceCodeInSuccess, navianceSignInFailure, newPasswordRequired };
export { login, completeNewPassword, appRefreshed, refreshSession, getIdToken, getJwtToken };
